import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Gauge, Send, Building2, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/KpiCard";
import { api, errMsg } from "@/lib/api";
import { fmtNum, fmtEur, kmlTone, todayISO } from "@/lib/format";

const empty = { date: todayISO(), vehicles_out: "", kilometers: "", total_amount: "", fuel_price_per_liter: "" };

export default function OperatorEntry() {
  const navigate = useNavigate();
  const prefill = useLocation().state?.prefill;
  const [station, setStation] = useState(null);
  const [form, setForm] = useState(() => ({ ...empty, ...(prefill || {}) }));
  useEffect(() => {
    if (prefill) window.history.replaceState({}, "");
  }, [prefill]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/stations").then((r) => setStation(r.data[0] || null));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const km = Number(form.kilometers), cost = Number(form.total_amount), pr = Number(form.fuel_price_per_liter);
  const lt = pr > 0 ? cost / pr : 0;
  const kml = lt > 0 ? km / lt : 0;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/entries", {
        date: form.date, kilometers: km, total_amount: cost, fuel_price_per_liter: pr,
        vehicles_out: Number(form.vehicles_out),
      });
      toast.success("Record inviato. In attesa di approvazione.");
      setForm({ ...empty, fuel_price_per_liter: form.fuel_price_per_liter });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-up">
      <PageHeader title="Inserimento giornaliero" subtitle="Registra i chilometri e il carburante totale della giornata." />
      <div className="grid gap-6 lg:grid-cols-12">
        <form onSubmit={submit} className="space-y-5 rounded-xl border border-slate-200 bg-white p-6 lg:col-span-7" data-testid="operator-entry-form">
          <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
            <Building2 className="h-4 w-4" /> Stazione: <span className="font-semibold text-slate-900" data-testid="operator-station-name">{station?.name || "—"}</span>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Data</Label>
              <Input type="date" required max={todayISO()} value={form.date} onChange={set("date")} data-testid="operator-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Mezzi usciti in rotta</Label>
              <Input type="number" step="1" min="1" required placeholder="es. 12" value={form.vehicles_out} onChange={set("vehicles_out")} data-testid="operator-vehicles-out-input" />
            </div>
            <div className="space-y-1.5">
              <Label>KM percorsi</Label>
              <Input type="number" step="0.1" min="0.1" required placeholder="es. 1250" value={form.kilometers} onChange={set("kilometers")} data-testid="operator-km-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Importo totale gasolio (€)</Label>
              <Input type="number" step="0.01" min="0.01" required placeholder="es. 642,50" value={form.total_amount} onChange={set("total_amount")} data-testid="operator-amount-input" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Prezzo gasolio (€/litro)</Label>
              <Input type="number" step="0.001" min="0.001" required placeholder="es. 1,689" value={form.fuel_price_per_liter} onChange={set("fuel_price_per_liter")} data-testid="operator-price-input" />
            </div>
          </div>
          <Button type="submit" className="w-full sm:w-auto" disabled={saving} data-testid="operator-submit-entry-button">
            <Send className="mr-2 h-4 w-4" /> {saving ? "Invio..." : "Invia record"}
          </Button>
        </form>

        <div className="space-y-4 lg:col-span-5">
          <div className={`rounded-xl border p-6 transition-colors ${kmlTone(kml)}`}>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider"><Gauge className="h-4 w-4" /> Consumo medio in tempo reale</div>
            <p className="font-mono-num mt-4 text-5xl font-semibold tracking-tight" data-testid="operator-live-kml-value">{fmtNum(kml, 2)}</p>
            <p className="mt-1 text-sm">km/l</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Litri calcolati</p>
              <p className="font-mono-num mt-2 text-xl font-semibold" data-testid="operator-live-liters-value">{fmtNum(lt, 2)} L</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Costo / km</p>
              <p className="font-mono-num mt-2 text-xl font-semibold" data-testid="operator-live-costkm-value">{km > 0 ? fmtEur(cost / km) : "—"}</p>
            </div>
            <div className="col-span-2 flex items-center justify-between rounded-xl border border-slate-200 bg-white p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Importo totale carburante</p>
              <p className="font-mono-num text-xl font-semibold" data-testid="operator-live-cost-value">{fmtEur(cost)}</p>
            </div>
            <div className="col-span-2 flex items-center justify-between rounded-xl border border-slate-200 bg-white p-5">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500"><Truck className="h-4 w-4" /> Media km per mezzo</p>
              <p className="font-mono-num text-xl font-semibold" data-testid="operator-live-kmvehicle-value">{Number(form.vehicles_out) > 0 ? fmtNum(km / Number(form.vehicles_out), 1) : "—"}</p>
            </div>
          </div>
          <button onClick={() => navigate("/stazione/storico")} data-testid="go-history-button" className="w-full rounded-xl border border-dashed border-slate-300 p-4 text-sm font-medium text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-900">
            Vedi lo storico e il riepilogo mensile →
          </button>
        </div>
      </div>
    </div>
  );
}
